import api from '../../../services/api';
import { normalizeTransportRouteDetail, normalizeTransportRoutes, TransportRouteDetailDto, TransportRouteSummaryDto } from '../../../dto/transport';

export interface TransportRouteListFilters {
  search?: string;
  transportCompany?: string;
  products?: string;
  status?: string;
  orderDateFrom?: string;
  orderDateTo?: string;
}

export async function listTransportRoutes(
  filters: TransportRouteListFilters = {},
): Promise<TransportRouteSummaryDto[]> {
  const response = await api.get('/transport-routes', { params: filters });
  return normalizeTransportRoutes(response.data, 'transportRoutes.list');
}

export async function listTransportRoutesForSelection(): Promise<TransportRouteSummaryDto[]> {
  const response = await api.get('/transport-routes');
  return normalizeTransportRoutes(response.data, 'transportRoutes.selection');
}

export async function fetchTransportRoute(transportId: string): Promise<TransportRouteDetailDto> {
  const response = await api.get(`/transport-routes/${encodeURIComponent(transportId)}`);
  return normalizeTransportRouteDetail(response.data, 'transportRoutes.detail');
}

export async function createTransportRoute(payload: Record<string, unknown>) {
  return api.post('/transport-routes', payload);
}

export async function updateTransportRoute(transportId: string, payload: Record<string, unknown>) {
  return api.put(`/transport-routes/${encodeURIComponent(transportId)}`, payload);
}

/** Update, then return the canonical detail read model rather than local payload assumptions. */
export async function updateTransportRouteAndRefetch(
  transportId: string,
  payload: Record<string, unknown>,
): Promise<TransportRouteDetailDto> {
  await updateTransportRoute(transportId, payload);
  return fetchTransportRoute(transportId);
}

/**
 * Delete a transport (shared or single-order). Transports live under
 * /transport-routes regardless of how many orders they carry, so the deletion
 * seam belongs here rather than under any order-scoped API module.
 */
export async function deleteTransportRoute(transportId: string): Promise<void> {
  await api.delete(`/transport-routes/${encodeURIComponent(transportId)}`);
}

export type UnlinkTransportOrderResult =
  | { deleted: true; route: null }
  | { deleted: false; route: TransportRouteDetailDto };

/**
 * Unlink through the canonical TransportOrderAllocation endpoint. If the
 * transport survives, immediately refetch its canonical detail projection.
 */
export async function unlinkTransportOrderAndRefetch(
  transportId: string,
  orderId: string,
): Promise<UnlinkTransportOrderResult> {
  const response = await api.delete(
    `/transport-routes/${encodeURIComponent(transportId)}/orders/${encodeURIComponent(orderId)}`,
  );
  const deleted = response.data?.deleted === true;

  if (deleted) return { deleted: true, route: null };
  return { deleted: false, route: await fetchTransportRoute(transportId) };
}

/** Duplicate the persisted plan; the caller navigates to its canonical detail. */
export async function duplicateTransportRoute(transportId: string): Promise<TransportRouteDetailDto> {
  const response = await api.post(`/transport-routes/${encodeURIComponent(transportId)}/duplicate`);
  return normalizeTransportRouteDetail(response.data, 'transportRoutes.duplicate');
}
